/**
 * Extracts a vibrant accent color from an image URL using a canvas.
 * Two-pass algorithm:
 *   Pass 1 — pick the most saturated+bright color (ideal for colourful covers).
 *   Pass 2 — if the image is too dark, find the dominant *hue* and
 *             reconstruct a vivid version of it at fixed saturation+lightness.
 * Returns an `rgb(r, g, b)` string. Falls back to the gaming accent color.
 */
const colorCache = new Map<string, string>();

/** Convert RGB → HSL (all 0-1). */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

/** Convert HSL (0-1) → RGB (0-255). */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1/3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1/3) * 255),
  ];
}

export function extractDominantColor(src: string): Promise<string> {
  if (colorCache.has(src)) return Promise.resolve(colorCache.get(src)!);

  return new Promise((resolve) => {
    const fallback = 'rgb(249, 115, 22)'; // gaming-accent orange
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) { resolve(fallback); return; }

        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        // ── Collect all non-transparent pixels with HSL info ──
        type Px = { r: number; g: number; b: number; h: number; s: number; l: number };
        const pixels: Px[] = [];
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
          if (a < 128) continue;
          const [h, s, l] = rgbToHsl(r, g, b);
          pixels.push({ r, g, b, h, s, l });
        }

        if (pixels.length === 0) { colorCache.set(src, fallback); resolve(fallback); return; }

        // ── PASS 1: vivid pixels only (sat > 0.25 AND lightness 15%-85%) ──
        const vivid = pixels.filter(p => p.s > 0.25 && p.l > 0.15 && p.l < 0.85);

        let result: string | null = null;

        if (vivid.length >= pixels.length * 0.03) {
          // Enough vivid pixels — bucket by quantised RGB and pick best
          const buckets = new Map<string, { r: number; g: number; b: number; count: number; sat: number; lum: number }>();
          for (const p of vivid) {
            const q = 24;
            const key = `${Math.round(p.r / q) * q},${Math.round(p.g / q) * q},${Math.round(p.b / q) * q}`;
            const b = buckets.get(key);
            if (b) { b.r += p.r; b.g += p.g; b.b += p.b; b.count++; b.sat += p.s; b.lum += p.l; }
            else buckets.set(key, { r: p.r, g: p.g, b: p.b, count: 1, sat: p.s, lum: p.l });
          }

          let best = { r: 0, g: 0, b: 0, score: -1 };
          for (const bk of buckets.values()) {
            const avgSat = bk.sat / bk.count;
            const avgLum = bk.lum / bk.count;
            // Strongly prefer sat + brightness; penalise dark heavily
            const lumWeight = avgLum < 0.35 ? avgLum / 0.35 : 1;
            const score = bk.count * avgSat * avgSat * lumWeight;
            if (score > best.score) {
              best = { r: Math.round(bk.r / bk.count), g: Math.round(bk.g / bk.count), b: Math.round(bk.b / bk.count), score };
            }
          }

          // Ensure minimum brightness
          const max = Math.max(best.r, best.g, best.b);
          if (max < 140 && max > 0) {
            const scale = 140 / max;
            best.r = Math.min(255, Math.round(best.r * scale));
            best.g = Math.min(255, Math.round(best.g * scale));
            best.b = Math.min(255, Math.round(best.b * scale));
          }

          result = `rgb(${best.r}, ${best.g}, ${best.b})`;
        }

        // ── PASS 2: image is very dark/desaturated → hue-based approach ──
        if (!result) {
          // Find the dominant hue among all non-grey pixels (sat > 0.08)
          const chromatic = pixels.filter(p => p.s > 0.08);
          if (chromatic.length === 0) {
            colorCache.set(src, fallback);
            resolve(fallback);
            return;
          }

          // Bucket by hue (36 buckets = 10° each)
          const hueBuckets = new Array(36).fill(0);
          for (const p of chromatic) {
            const idx = Math.min(35, Math.floor(p.h * 36));
            hueBuckets[idx]++;
          }

          let bestIdx = 0, bestCount = 0;
          for (let i = 0; i < 36; i++) {
            // count including neighbours for smoothing
            const count = hueBuckets[i] + (hueBuckets[(i + 35) % 36] ?? 0) * 0.5 + (hueBuckets[(i + 1) % 36] ?? 0) * 0.5;
            if (count > bestCount) { bestCount = count; bestIdx = i; }
          }

          // Reconstruct a vivid color at that hue
          const hue = (bestIdx + 0.5) / 36;
          const [vr, vg, vb] = hslToRgb(hue, 0.75, 0.55);
          result = `rgb(${vr}, ${vg}, ${vb})`;
        }

        colorCache.set(src, result);
        resolve(result);
      } catch {
        resolve(fallback);
      }
    };

    img.onerror = () => resolve(fallback);
    img.src = src;
  });
}
